import { Injectable } from '@angular/core';

export interface WifiStatusData {
  propertyId: number;
  propertyName: string;
  propertyColor: string;
  routerModel: string;
  routerIp: string;
  status: 'online' | 'offline' | 'degraded';
  lastPing: string;
  latencyMs: number;
  signalStrength: number;
  connectedDevices: number;
  accessPointCount: number;
}

export interface WifiTableRow {
  id: number;
  'Property': { value: string; action: null; color: string; addressId: number };
  'Access Points': { value: string; action: null };
  'Wi-Fi Status': { value: string; action: null; icon: string; showIconOnRight: true };
  'last-column': { value: number; action: null; hasChevron: true };
}

@Injectable({ providedIn: 'root' })
export class MarketingWifiService {

  private readonly statusIcons: Record<WifiStatusData['status'], string> = {
    online: 'assets/img/checkmark-circle-green.svg',
    offline: 'assets/img/close-circle-red.svg',
    degraded: 'assets/img/yellow-alert.svg'
  };

  private readonly statusLabels: Record<WifiStatusData['status'], string> = {
    online: 'Online',
    offline: 'Offline',
    degraded: 'Degraded'
  };

  private readonly dummyData: WifiStatusData[] = [
    { propertyId: 1, propertyName: 'Beach House', propertyColor: '#4CAF50', routerModel: 'Ubiquiti UniFi AP-AC-Pro', routerIp: '192.168.1.1', status: 'online', lastPing: '2026-02-20T14:30:00Z', latencyMs: 12, signalStrength: 92, connectedDevices: 8, accessPointCount: 2 },
    { propertyId: 2, propertyName: 'Mountain Cabin', propertyColor: '#2196F3', routerModel: 'Netgear Orbi RBK50', routerIp: '192.168.1.10', status: 'online', lastPing: '2026-02-20T14:28:00Z', latencyMs: 18, signalStrength: 85, connectedDevices: 3, accessPointCount: 1 },
    { propertyId: 3, propertyName: 'City Apartment', propertyColor: '#FF9800', routerModel: 'TP-Link Deco X60', routerIp: '192.168.0.1', status: 'degraded', lastPing: '2026-02-20T14:15:00Z', latencyMs: 145, signalStrength: 42, connectedDevices: 12, accessPointCount: 1 },
    { propertyId: 4, propertyName: 'Lake House', propertyColor: '#9C27B0', routerModel: 'Eero Pro 6E', routerIp: '192.168.4.1', status: 'online', lastPing: '2026-02-20T14:29:00Z', latencyMs: 8, signalStrength: 97, connectedDevices: 5, accessPointCount: 1 },
    { propertyId: 5, propertyName: 'Downtown Loft', propertyColor: '#F44336', routerModel: 'Google Nest Wifi Pro', routerIp: '192.168.5.1', status: 'offline', lastPing: '2026-02-19T22:45:00Z', latencyMs: 0, signalStrength: 0, connectedDevices: 0, accessPointCount: 0 }
  ];

  buildDummyRows(): WifiTableRow[] {
    return this.dummyData.map(data => this.buildRow(data));
  }

  getRouterStatus(propertyId: number): WifiStatusData | undefined {
    return this.dummyData.find(d => d.propertyId === propertyId);
  }

  getStatusIcon(status: WifiStatusData['status']): string {
    return this.statusIcons[status];
  }

  getStatusLabel(status: WifiStatusData['status']): string {
    return this.statusLabels[status];
  }

  filterRows(rows: WifiTableRow[], searchTerm: string): WifiTableRow[] {
    if (!searchTerm?.trim()) {
      return rows;
    }
    const term = searchTerm.toLowerCase();
    return rows.filter(row => row['Property'].value.toLowerCase().includes(term));
  }

  getPropertiesWithoutSensor(allPropertyIds: number[]): number[] {
    const withSensor = new Set(
      this.dummyData
        .filter(d => d.accessPointCount > 0)
        .map(d => d.propertyId)
    );
    return allPropertyIds.filter(id => !withSensor.has(id));
  }

  private buildRow(data: WifiStatusData): WifiTableRow {
    return {
      id: data.propertyId,
      'Property': { value: data.propertyName, action: null, color: data.propertyColor, addressId: data.propertyId },
      'Access Points': { value: data.accessPointCount.toString(), action: null },
      'Wi-Fi Status': { value: this.statusLabels[data.status], action: null, icon: this.statusIcons[data.status], showIconOnRight: true as const },
      'last-column': { value: data.propertyId, action: null, hasChevron: true as const }
    };
  }
}
